import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import Can from '../../../components/Can';
import ClienteInlineSearchField from '../../../components/Tesoreria/ClienteInlineSearchField';
import ListaPrecioSearch from '../../../components/ListaPrecioSearch';
import SucursalVentaSearch from '../../../components/SucursalVentaSearch';
import TipoMonedaSearch from '../../../components/TipoMonedaSearch';
import { ROUTES } from '../../../config/routes';
import contratoVentaService, {
  CONTRATO_PERIODICIDADES,
  ContratoCertificacion,
  ContratoCreateUpdate,
  ContratoDetalle,
  ContratoItem,
  ContratoPeriodo,
} from '../../../services/contratoVentaService';
import ContratoCertificacionesTab from './ContratoCertificacionesTab';
import ContratoItemsSection from './ContratoItemsSection';
import ContratoPeriodosTab from './ContratoPeriodosTab';
import ContratoVariablesChips from './ContratoVariablesChips';
import {
  CONTRATO_FIELD_DESC,
  CONTRATO_FIELD_OBS,
  captureContratoPlantillaCaret,
  insertAtCaret,
  restoreContratoPlantillaCaret,
  type ContratoPlantillaCaret,
} from './contratoPlantillaVariables';

interface FormState {
  nroCliente: number;
  nombreCliente: string;
  codigoMoneda: number;
  monedaDescripcion: string;
  codigoLista: string;
  nroSucursalVenta: number;
  sucursalNombre: string;
  fechaInicio: string;
  fechaFin: string;
  periodicidad: string;
  modoFacturacion: string;
  diaFacturacion: string;
  nroOrdenCompraCliente: string;
  observaciones: string;
  estado: string;
  items: ContratoItem[];
}

const today = () => new Date().toISOString().slice(0, 10);
const yearEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 11, 31).toISOString().slice(0, 10);
};

const defaultForm = (): FormState => ({
  nroCliente: 0,
  nombreCliente: '',
  codigoMoneda: 0,
  monedaDescripcion: 'Pesos',
  codigoLista: '',
  nroSucursalVenta: 0,
  sucursalNombre: '',
  fechaInicio: today(),
  fechaFin: yearEnd(),
  periodicidad: 'Mensual',
  modoFacturacion: 'FinPeriodo',
  diaFacturacion: '',
  nroOrdenCompraCliente: '',
  observaciones: '',
  estado: 'Borrador',
  items: [],
});

const ContratoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'nuevo';
  const contratoId = isNew ? 0 : Number(id);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState<FormState>(defaultForm());
  const [periodos, setPeriodos] = useState<ContratoPeriodo[]>([]);
  const [certificaciones, setCertificaciones] = useState<ContratoCertificacion[]>([]);
  const [numero, setNumero] = useState<number | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [monedaOpen, setMonedaOpen] = useState(false);
  const [sucursalOpen, setSucursalOpen] = useState(false);
  const [listaOpen, setListaOpen] = useState(false);
  const lastPlantillaCaretRef = useRef<ContratoPlantillaCaret | null>(null);

  const trackPlantillaCaret = useCallback((caret: ContratoPlantillaCaret) => {
    lastPlantillaCaretRef.current = caret;
  }, []);

  const handleInsertVariable = useCallback(
    (variable: string) => {
      const caret = captureContratoPlantillaCaret(lastPlantillaCaretRef.current);
      if (!caret) {
        enqueueSnackbar(
          'Posicioná el cursor en Observaciones o en la descripción de un producto',
          { variant: 'info' },
        );
        return;
      }

      if (caret.field === CONTRATO_FIELD_OBS) {
        const next = insertAtCaret(form.observaciones, caret.start, caret.end, variable);
        setForm((prev) => ({ ...prev, observaciones: next.value }));
        lastPlantillaCaretRef.current = {
          field: CONTRATO_FIELD_OBS,
          start: next.caret,
          end: next.caret,
        };
        restoreContratoPlantillaCaret(caret, next.caret);
        return;
      }

      if (caret.itemIndex == null) return;
      const item = form.items[caret.itemIndex];
      if (!item) return;
      const next = insertAtCaret(item.descripcionPlantilla, caret.start, caret.end, variable);
      setForm((prev) => ({
        ...prev,
        items: prev.items.map((it, i) =>
          i === caret.itemIndex ? { ...it, descripcionPlantilla: next.value } : it,
        ),
      }));
      lastPlantillaCaretRef.current = {
        field: CONTRATO_FIELD_DESC,
        itemIndex: caret.itemIndex,
        start: next.caret,
        end: next.caret,
      };
      restoreContratoPlantillaCaret(caret, next.caret);
    },
    [enqueueSnackbar, form.items, form.observaciones],
  );

  const load = useCallback(async () => {
    if (!contratoId) return;
    try {
      setLoading(true);
      const d: ContratoDetalle = await contratoVentaService.getById(contratoId);
      setNumero(d.numero);
      setForm({
        nroCliente: d.nroCliente,
        nombreCliente: d.nombreCliente || '',
        codigoMoneda: d.codigoMoneda,
        monedaDescripcion: d.monedaDescripcion || '',
        codigoLista: d.codigoLista || '',
        nroSucursalVenta: d.nroSucursalVenta,
        sucursalNombre: String(d.nroSucursalVenta),
        fechaInicio: String(d.fechaInicio).slice(0, 10),
        fechaFin: String(d.fechaFin).slice(0, 10),
        periodicidad: d.periodicidad,
        modoFacturacion: d.modoFacturacion,
        diaFacturacion: d.diaFacturacion != null ? String(d.diaFacturacion) : '',
        nroOrdenCompraCliente: d.nroOrdenCompraCliente || '',
        observaciones: d.observaciones || '',
        estado: d.estado,
        items: d.items,
      });
      setPeriodos(d.periodos || []);
      setCertificaciones(d.certificaciones || []);
    } catch {
      enqueueSnackbar('Error al cargar el contrato', { variant: 'error' });
      navigate(ROUTES.ventas.contratos.list);
    } finally {
      setLoading(false);
    }
  }, [contratoId, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (!isNew) load();
  }, [isNew, load]);

  const buildDto = (activar?: boolean): ContratoCreateUpdate => ({
    nroCliente: form.nroCliente,
    codigoMoneda: form.codigoMoneda,
    codigoLista: form.codigoLista || null,
    nroSucursalVenta: form.nroSucursalVenta,
    fechaInicio: form.fechaInicio,
    fechaFin: form.fechaFin,
    periodicidad: form.periodicidad,
    modoFacturacion: form.modoFacturacion,
    diaFacturacion: form.diaFacturacion ? Number(form.diaFacturacion) : null,
    nroOrdenCompraCliente: form.nroOrdenCompraCliente || null,
    observaciones: form.observaciones || null,
    activar: activar === true,
    items: form.items.map((it, idx) => ({ ...it, orden: idx + 1 })),
  });

  const handleSave = async (activar?: boolean) => {
    if (!form.nroCliente) {
      enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' });
      return;
    }
    if (!form.nroSucursalVenta) {
      enqueueSnackbar('Seleccioná una sucursal de venta', { variant: 'warning' });
      return;
    }
    if (form.items.length === 0) {
      enqueueSnackbar('Agregá al menos un producto', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const dto = buildDto(activar);
      if (isNew) {
        await contratoVentaService.create(dto);
        enqueueSnackbar('Contrato creado', { variant: 'success' });
      } else {
        await contratoVentaService.update(contratoId, dto);
        enqueueSnackbar('Contrato actualizado', { variant: 'success' });
      }
      navigate(ROUTES.ventas.contratos.list);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEstado = async (estado: string) => {
    try {
      await contratoVentaService.cambiarEstado(contratoId, estado);
      enqueueSnackbar(`Estado: ${estado}`, { variant: 'success' });
      await load();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cambiar estado', {
        variant: 'error',
      });
    }
  };

  const readOnly = form.estado === 'Anulado';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <IconButton onClick={() => navigate(ROUTES.ventas.contratos.list)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', flex: 1 }}>
              {isNew ? 'Nuevo contrato' : `Contrato Nº ${numero}`}
            </Typography>
            {!isNew && (
              <Typography variant="body2" color="text.secondary">
                Estado: <strong>{form.estado}</strong>
              </Typography>
            )}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ClienteInlineSearchField
                displayValue={
                  form.nroCliente
                    ? `${form.nroCliente} — ${form.nombreCliente || ''}`.trim()
                    : undefined
                }
                onSelect={(cli) =>
                  setForm((f) => ({
                    ...f,
                    nroCliente: cli?.nroCliente || 0,
                    nombreCliente: cli?.nombre || '',
                  }))
                }
                disabled={readOnly}
                label="Cliente"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Moneda"
                fullWidth
                size="small"
                value={form.monedaDescripcion || form.codigoMoneda}
                InputProps={{
                  readOnly: true,
                  endAdornment: !readOnly ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setMonedaOpen(true)}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Sucursal venta"
                fullWidth
                size="small"
                value={form.sucursalNombre || (form.nroSucursalVenta ? String(form.nroSucursalVenta) : '')}
                InputProps={{
                  readOnly: true,
                  endAdornment: !readOnly ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSucursalOpen(true)}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                label="Inicio"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.fechaInicio}
                onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                label="Fin"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.fechaFin}
                onChange={(e) => setForm({ ...form, fechaFin: e.target.value })}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Periodicidad"
                fullWidth
                size="small"
                value={form.periodicidad}
                onChange={(e) => setForm({ ...form, periodicidad: e.target.value })}
                disabled={readOnly}
              >
                {CONTRATO_PERIODICIDADES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Modo facturación"
                fullWidth
                size="small"
                value={form.modoFacturacion}
                onChange={(e) => setForm({ ...form, modoFacturacion: e.target.value })}
                disabled={readOnly}
              >
                <MenuItem value="InicioPeriodo">Al inicio del período</MenuItem>
                <MenuItem value="FinPeriodo">Al fin del período</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Día facturación"
                fullWidth
                size="small"
                type="number"
                value={form.diaFacturacion}
                onChange={(e) => setForm({ ...form, diaFacturacion: e.target.value })}
                disabled={readOnly}
                inputProps={{ min: 1, max: 31 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Lista de precios"
                fullWidth
                size="small"
                value={form.codigoLista}
                InputProps={{
                  readOnly: true,
                  endAdornment: !readOnly ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setListaOpen(true)}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
                helperText="Opcional; para ítems con modo Lista"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="OC cliente"
                fullWidth
                size="small"
                value={form.nroOrdenCompraCliente}
                onChange={(e) => setForm({ ...form, nroOrdenCompraCliente: e.target.value })}
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observaciones"
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                onSelect={(e) => {
                  const t = e.target as HTMLInputElement | HTMLTextAreaElement;
                  trackPlantillaCaret({
                    field: CONTRATO_FIELD_OBS,
                    start: t.selectionStart ?? 0,
                    end: t.selectionEnd ?? 0,
                  });
                }}
                onBlur={(e) => {
                  trackPlantillaCaret({
                    field: CONTRATO_FIELD_OBS,
                    start: e.target.selectionStart ?? 0,
                    end: e.target.selectionEnd ?? 0,
                  });
                }}
                disabled={readOnly}
                inputProps={{ 'data-contrato-field': CONTRATO_FIELD_OBS }}
              />
            </Grid>
          </Grid>

          {!readOnly && (
            <Box sx={{ mt: 1 }}>
              <ContratoVariablesChips onInsert={handleInsertVariable} />
            </Box>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2, mb: 2 }}>
            <Tab label="Productos" />
            <Tab label="Períodos" disabled={isNew} />
            <Tab label="Certificaciones" disabled={isNew} />
          </Tabs>

          {tab === 0 && (
            <ContratoItemsSection
              items={form.items}
              onChange={(items) => setForm({ ...form, items })}
              onInsertVariable={handleInsertVariable}
              onTrackPlantillaCaret={trackPlantillaCaret}
              readOnly={readOnly}
            />
          )}
          {tab === 1 && !isNew && (
            <ContratoPeriodosTab
              contratoId={contratoId}
              periodos={periodos}
              onUpdated={setPeriodos}
              readOnly={readOnly}
            />
          )}
          {tab === 2 && !isNew && (
            <ContratoCertificacionesTab
              contratoId={contratoId}
              items={form.items}
              certificaciones={certificaciones}
              onChanged={load}
              readOnly={readOnly}
            />
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            {!isNew && form.estado === 'Borrador' && (
              <Can permiso="ventas.contratos.editar">
                <Button variant="outlined" onClick={() => handleEstado('Vigente')}>
                  Activar
                </Button>
              </Can>
            )}
            {!isNew && form.estado === 'Vigente' && (
              <Can permiso="ventas.contratos.editar">
                <Button variant="outlined" color="warning" onClick={() => handleEstado('Suspendido')}>
                  Suspender
                </Button>
              </Can>
            )}
            {!isNew && form.estado === 'Suspendido' && (
              <Can permiso="ventas.contratos.editar">
                <Button variant="outlined" onClick={() => handleEstado('Vigente')}>
                  Reanudar
                </Button>
              </Can>
            )}
            {!isNew && form.estado !== 'Anulado' && form.estado !== 'Finalizado' && (
              <Can permiso="ventas.contratos.anular">
                <Button variant="outlined" color="error" onClick={() => handleEstado('Anulado')}>
                  Anular
                </Button>
              </Can>
            )}
            {!readOnly && (
              <Can permiso={isNew ? 'ventas.contratos.nuevo' : 'ventas.contratos.editar'}>
                <Button variant="contained" onClick={() => handleSave(false)} disabled={saving}>
                  Guardar
                </Button>
              </Can>
            )}
            {isNew && (
              <Can permiso="ventas.contratos.nuevo">
                <Button variant="contained" color="success" onClick={() => handleSave(true)} disabled={saving}>
                  Guardar y activar
                </Button>
              </Can>
            )}
          </Stack>
        </CardContent>
      </Card>

      <TipoMonedaSearch
        open={monedaOpen}
        onClose={() => setMonedaOpen(false)}
        onSelect={(m: any) => {
          setForm((f) => ({
            ...f,
            codigoMoneda: Number(m.codigoMoneda),
            monedaDescripcion: m.descripcion || String(m.codigoMoneda),
          }));
          setMonedaOpen(false);
        }}
      />
      <SucursalVentaSearch
        open={sucursalOpen}
        onClose={() => setSucursalOpen(false)}
        onSelect={(s) => {
          setForm((f) => ({
            ...f,
            nroSucursalVenta: s.nroSuc,
            sucursalNombre: s.nombreSucursal || String(s.nroSuc),
          }));
          setSucursalOpen(false);
        }}
      />
      <ListaPrecioSearch
        open={listaOpen}
        onClose={() => setListaOpen(false)}
        onSelect={(l) => {
          setForm((f) => ({ ...f, codigoLista: l.codigoLista }));
          setListaOpen(false);
        }}
      />
    </Box>
  );
};

export default ContratoForm;
